import React, { useEffect, useState } from 'react';
import { kycAPI } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import { ShieldCheck, ShieldAlert, Clock, Upload, Check, X, ArrowRight, ArrowLeft, User, MapPin, FileText, Camera, AlertCircle } from 'lucide-react';

// Compress + convert File to base64 data URL
async function fileToBase64(file, maxDim = 1400, quality = 0.82) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error('No file'));
    if (file.size > 8 * 1024 * 1024) return reject(new Error('File too large (max 8MB)'));
    const img = new Image();
    const reader = new FileReader();
    reader.onload = e => { img.src = e.target.result; };
    reader.onerror = reject;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const scale = Math.min(maxDim / width, maxDim / height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const STATUS_META = {
  not_started: { color: 'text-gray-400', bg: 'bg-[#1a2823]', border: 'border-[#1a2823]', icon: ShieldAlert, label: 'Not Verified' },
  pending:     { color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30', icon: Clock, label: 'Under Review' },
  approved:    { color: 'text-[#00e676]', bg: 'bg-[#00e676]/10', border: 'border-[#00e676]/30', icon: ShieldCheck, label: 'Verified' },
  rejected:    { color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', icon: X, label: 'Rejected' }
};

const COUNTRIES = ['United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 'France', 'Italy', 'Spain', 'Netherlands', 'Switzerland', 'Singapore', 'UAE', 'India', 'Japan', 'Brazil', 'Mexico', 'South Africa', 'Other'];

const KYC = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    full_name: '', date_of_birth: '', nationality: '', phone: '',
    address_line1: '', address_line2: '', city: '', state: '', postal_code: '', country: '',
    occupation: '', source_of_funds: '', annual_income: '',
    id_type: 'passport', id_number: '',
    id_front: '', id_back: '', selfie: '', proof_of_address: ''
  });
  const [uploading, setUploading] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const load = async () => {
    try { setStatus(await kycAPI.me()); } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (user?.name && !form.full_name) setForm(f => ({ ...f, full_name: user.name }));
  }, [user, form.full_name]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleUpload = async (field, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(field);
    try {
      const b64 = await fileToBase64(file);
      set(field, b64);
    } catch (err) {
      toast({ title: 'Upload failed', description: err.message });
    } finally {
      setUploading('');
    }
  };

  const canProceed = () => {
    if (step === 1) return form.full_name && form.date_of_birth && form.nationality && form.phone;
    if (step === 2) return form.address_line1 && form.city && form.postal_code && form.country;
    if (step === 3) return form.id_type && form.id_number && form.id_front && form.selfie && (form.id_type === 'passport' || form.id_back);
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await kycAPI.submit(form);
      toast({ title: 'KYC submitted', description: 'Your documents are under review.' });
      await load();
      setStep(1);
    } catch (err) {
      toast({ title: 'Submission failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="text-sm text-gray-500">Loading…</div>;

  const st = status?.status || 'not_started';
  const meta = STATUS_META[st];
  const StIcon = meta.icon;

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <ShieldCheck size={22} className="text-[#00e676]"/> KYC Verification
        </h1>
        <p className="text-sm text-gray-400">Verify your identity to unlock full trading limits and faster withdrawals.</p>
      </div>

      {/* Status card */}
      <div className={`p-5 rounded-xl border ${meta.border} ${meta.bg} flex items-center gap-4`}>
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${meta.bg} border ${meta.border} ${meta.color}`}>
          <StIcon size={22}/>
        </div>
        <div className="flex-1">
          <div className={`text-sm font-bold ${meta.color}`}>Status: {meta.label}</div>
          <div className="text-xs text-gray-400 mt-0.5">
            {st === 'not_started' && 'Complete the 3-step verification below to get verified.'}
            {st === 'pending' && (status?.message || 'Your documents are being reviewed. This typically takes up to 24 hours.')}
            {st === 'approved' && 'Congratulations! Your account is fully verified.'}
            {st === 'rejected' && `Rejection reason: ${status?.rejection_reason || 'Please resubmit with clearer documents.'}`}
          </div>
        </div>
      </div>

      {/* Approved snapshot */}
      {st === 'approved' && status && (
        <div className="p-6 rounded-xl border border-[#1a2823] bg-[#0a1210]">
          <h3 className="text-sm text-white font-bold mb-4">Verified Information</h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <Info label="Full Name" value={status.full_name}/>
            <Info label="Date of Birth" value={status.date_of_birth}/>
            <Info label="Nationality" value={status.nationality}/>
            <Info label="Country" value={status.country}/>
            <Info label="Address" value={`${status.address_line1 || ''} ${status.city || ''}, ${status.postal_code || ''}`}/>
            <Info label="ID Type" value={status.id_type?.replace('_', ' ')}/>
            <Info label="Submitted" value={status.submitted_at ? new Date(status.submitted_at).toLocaleDateString() : '—'}/>
            <Info label="Verified" value={status.reviewed_at ? new Date(status.reviewed_at).toLocaleDateString() : '—'}/>
          </div>
        </div>
      )}

      {/* Multi-step form (only if not approved or rejected can retry) */}
      {(st === 'not_started' || st === 'rejected') && (
        <div className="p-6 rounded-xl border border-[#1a2823] bg-[#0a1210]">
          {/* Stepper */}
          <div className="flex items-center justify-between mb-6">
            {[
              { n: 1, label: 'Personal Info', icon: User },
              { n: 2, label: 'Address', icon: MapPin },
              { n: 3, label: 'Documents', icon: FileText }
            ].map((s, i, arr) => (
              <React.Fragment key={s.n}>
                <div className={`flex flex-col items-center gap-1 ${step >= s.n ? 'text-[#00e676]' : 'text-gray-500'}`}>
                  <div className={`w-9 h-9 rounded-full border flex items-center justify-center ${step >= s.n ? 'border-[#00e676] bg-[#00e676]/10' : 'border-[#1a2823]'}`}>
                    {step > s.n ? <Check size={16}/> : <s.icon size={16}/>}
                  </div>
                  <span className="text-[10px] font-semibold">{s.label}</span>
                </div>
                {i < arr.length - 1 && <div className={`flex-1 h-0.5 mx-3 ${step > s.n ? 'bg-[#00e676]' : 'bg-[#1a2823]'}`}/>}
              </React.Fragment>
            ))}
          </div>

          {/* Step 1: Personal */}
          {step === 1 && (
            <div className="grid md:grid-cols-2 gap-4">
              <TxtInput label="Full Legal Name *" value={form.full_name} onChange={v => set('full_name', v)} placeholder="As on your ID"/>
              <TxtInput label="Date of Birth *" type="date" value={form.date_of_birth} onChange={v => set('date_of_birth', v)}/>
              <SelInput label="Nationality *" value={form.nationality} onChange={v => set('nationality', v)} options={COUNTRIES}/>
              <TxtInput label="Phone Number *" value={form.phone} onChange={v => set('phone', v)} placeholder="+1 555 123 4567"/>
              <TxtInput label="Occupation" value={form.occupation} onChange={v => set('occupation', v)} placeholder="e.g. Software Engineer"/>
              <SelInput label="Source of Funds" value={form.source_of_funds} onChange={v => set('source_of_funds', v)} options={['Salary/Employment', 'Business Income', 'Investment', 'Inheritance', 'Savings', 'Other']}/>
              <SelInput label="Annual Income" value={form.annual_income} onChange={v => set('annual_income', v)} options={['< $25,000', '$25,000 - $50,000', '$50,000 - $100,000', '$100,000 - $250,000', '$250,000 - $1M', '> $1M']}/>
            </div>
          )}

          {/* Step 2: Address */}
          {step === 2 && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2"><TxtInput label="Address Line 1 *" value={form.address_line1} onChange={v => set('address_line1', v)} placeholder="Street address"/></div>
              <div className="md:col-span-2"><TxtInput label="Address Line 2" value={form.address_line2} onChange={v => set('address_line2', v)} placeholder="Apt, suite, floor (optional)"/></div>
              <TxtInput label="City *" value={form.city} onChange={v => set('city', v)}/>
              <TxtInput label="State/Region" value={form.state} onChange={v => set('state', v)}/>
              <TxtInput label="Postal Code *" value={form.postal_code} onChange={v => set('postal_code', v)}/>
              <SelInput label="Country *" value={form.country} onChange={v => set('country', v)} options={COUNTRIES}/>
            </div>
          )}

          {/* Step 3: Documents */}
          {step === 3 && (
            <div className="space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <SelInput label="ID Type *" value={form.id_type} onChange={v => set('id_type', v)} options={[
                  { value: 'passport', label: 'Passport' },
                  { value: 'national_id', label: 'National ID' },
                  { value: 'drivers_license', label: "Driver's License" }
                ]}/>
                <TxtInput label="ID Number *" value={form.id_number} onChange={v => set('id_number', v)} placeholder="As printed on your document"/>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <FileUpload label="ID Front *" hint="Photo of the front of your ID" field="id_front" value={form.id_front} onUpload={handleUpload} onClear={() => set('id_front', '')} uploading={uploading === 'id_front'}/>
                {form.id_type !== 'passport' && (
                  <FileUpload label="ID Back *" hint="Photo of the back of your ID" field="id_back" value={form.id_back} onUpload={handleUpload} onClear={() => set('id_back', '')} uploading={uploading === 'id_back'}/>
                )}
                <FileUpload label="Selfie with ID *" hint="Hold your ID next to your face" field="selfie" value={form.selfie} onUpload={handleUpload} onClear={() => set('selfie', '')} uploading={uploading === 'selfie'} icon={Camera}/>
                <FileUpload label="Proof of Address" hint="Utility bill or bank statement (< 3 months)" field="proof_of_address" value={form.proof_of_address} onUpload={handleUpload} onClear={() => set('proof_of_address', '')} uploading={uploading === 'proof_of_address'}/>
              </div>

              <div className="p-3 rounded-lg border border-amber-500/20 bg-amber-500/5 flex items-start gap-2 text-xs text-amber-400">
                <AlertCircle size={14} className="flex-shrink-0 mt-0.5"/>
                <span>Documents must be clear, non-blurry, and unedited. Files are compressed to JPEG for upload. This is a demo — do NOT upload real IDs.</span>
              </div>
            </div>
          )}

          {/* Nav */}
          <div className="flex items-center justify-between mt-6 pt-5 border-t border-[#141f1b]">
            <button onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1} className="px-4 py-2 rounded border border-[#1a2823] text-gray-300 text-sm font-semibold flex items-center gap-1.5 disabled:opacity-40 hover:border-[#00e676]/40"><ArrowLeft size={13}/> Back</button>
            {step < 3 ? (
              <button onClick={() => setStep(s => s + 1)} disabled={!canProceed()} className="btn-green px-5 py-2 rounded font-semibold text-sm flex items-center gap-1.5 disabled:opacity-50">Continue <ArrowRight size={13}/></button>
            ) : (
              <button onClick={submit} disabled={!canProceed() || submitting} className="btn-green px-5 py-2 rounded font-semibold text-sm disabled:opacity-50">
                {submitting ? 'Submitting…' : 'Submit for Verification'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const TxtInput = ({ label, value, onChange, placeholder, type = 'text' }) => (
  <div>
    <label className="text-[10px] text-gray-400 uppercase tracking-wider">{label}</label>
    <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="mt-1 w-full bg-[#050807] border border-[#1a2823] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00e676]"/>
  </div>
);

const SelInput = ({ label, value, onChange, options }) => (
  <div>
    <label className="text-[10px] text-gray-400 uppercase tracking-wider">{label}</label>
    <select value={value} onChange={e => onChange(e.target.value)} className="mt-1 w-full bg-[#050807] border border-[#1a2823] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00e676]">
      <option value="">Select…</option>
      {options.map(o => {
        const val = typeof o === 'string' ? o : o.value;
        const lab = typeof o === 'string' ? o : o.label;
        return <option key={val} value={val}>{lab}</option>;
      })}
    </select>
  </div>
);

const FileUpload = ({ label, hint, field, value, onUpload, onClear, uploading, icon: Ico = Upload }) => (
  <div>
    <label className="text-[10px] text-gray-400 uppercase tracking-wider">{label}</label>
    <div className="mt-1">
      {!value ? (
        <label className={`block cursor-pointer rounded-lg border border-dashed ${uploading ? 'border-[#00e676]' : 'border-[#1a2823] hover:border-[#00e676]/40'} bg-[#050807] p-4 text-center transition`}>
          <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(field, e)}/>
          <Ico size={22} className={`mx-auto mb-2 ${uploading ? 'text-[#00e676]' : 'text-gray-500'}`}/>
          <div className="text-xs text-gray-300 font-semibold">{uploading ? 'Processing…' : 'Click to upload'}</div>
          <div className="text-[10px] text-gray-500 mt-1">{hint}</div>
        </label>
      ) : (
        <div className="relative rounded-lg border border-[#1a2823] bg-[#050807] overflow-hidden">
          <img src={value} alt={label} className="w-full h-32 object-cover"/>
          <div className="absolute top-2 right-2 flex gap-1">
            <button onClick={onClear} className="p-1.5 rounded bg-[#050807]/80 border border-[#1a2823] text-gray-300 hover:text-red-400"><X size={12}/></button>
          </div>
          <div className="p-2 flex items-center gap-1.5 text-[10px] text-[#00e676] font-semibold">
            <Check size={11}/> Uploaded ({Math.round(value.length / 1024)}kb)
          </div>
        </div>
      )}
    </div>
  </div>
);

const Info = ({ label, value }) => (
  <div>
    <div className="text-[10px] text-gray-400 uppercase tracking-wider">{label}</div>
    <div className="mt-0.5 text-sm text-white">{value || '—'}</div>
  </div>
);

export default KYC;
